import { memo, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import MaskedInput from 'react-input-mask'

import {
    Box,
    Button,
    LinearProgress,
    makeStyles,
    MenuItem,
    Select,
    Typography,
    ButtonGroup,
    TextField,
    Checkbox,
    Tooltip
} from '@material-ui/core'

import LinkIcon from '@material-ui/icons/Link'
import PeopleAltIcon from '@material-ui/icons/LockOutlined'
import PublicIcon from '@material-ui/icons/PublicOutlined'

import { ConfirmationDialog, SpacedGroup } from 'UI/Components'
import { LinkTemplates } from './LinkTemplates'

import {
    useAuth,
    useBoolean,
    useCopySharedLink,
    useNotification,
    useOrgFeatureFlags,
    usePlaybookShare,
    ShareProps,
    useServiceUsage,
    logVisibilityChanging
} from 'hooks'
import { PlaybookType } from 'app/types'
import { capitalize, formatTime, logToAnalytics, option } from 'modules'
import { TeamIcon } from 'UI/Components'

type VisibilityLevelType = ShareProps['visibilityLevel']
type OptionType = {
    value: VisibilityLevelType
    label: string
}

const VisibiltyLevel = {
    restricted: 'restricted',
    anyoneInOrg: 'anyoneInOrg',
    anyone: 'anyone'
}

const generateStartValue = (time: number) => {
    const value = Math.floor(time)
    const data = formatTime(value).split(':')
    if (data.length < 3) data.unshift('00')

    return data.join(':')
}

const useStyles = makeStyles(theme => ({
    shareLink: {
        borderRadius: '4px',
        background: 'white'
    },
    selectRoot: {
        '& ul': {
            width: '225px',
            padding: theme.spacing(1),
            '& li': {
                width: '100%',
                fontWeight: theme.fontWeightBold,
                fontSize: '14px',
                padding: theme.spacing(1.7, 2)
            }
        },
        '& svg': {
            color: 'inherit'
        }
    },
    selectedItem: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.primary,
        paddingBottom: 3
    },
    linearProgress: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: ' 100%',
        borderRadius: '4px'
    },
    icon: {
        color: theme.palette.text.primary
    },
    roundIcon: {
        background: '#EDEEEF',
        borderRadius: '50%',
        minWidth: '40px',
        minHeight: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px',

        '& svg': {
            color: 'inherit'
        }
    },
    select: {
        fontSize: '12px',
        fontWeight: theme.fontWeightBold,
        paddingBottom: 0,
        '&:before, &:after': {
            display: 'none'
        }
    },
    copyLinkButton: {
        whiteSpace: 'nowrap'
    },
    popper: {
        zIndex: theme.zIndex.modal + 1
    }
}))

const getPublicityList = (orgName: string, isPrivateAccount: boolean) =>
    [
        option(VisibiltyLevel.anyone, 'Public'),
        ...(isPrivateAccount ? [] : [option(VisibiltyLevel.anyoneInOrg, capitalize(orgName))]),
        option(VisibiltyLevel.restricted, 'Restricted')
    ] as Array<OptionType>

type GetPublicityStaticProps = {
    id: VisibilityLevelType
    target?: string
}

const getPublicityStaticData = ({ id, target }: GetPublicityStaticProps) => {
    const list = {
        [VisibiltyLevel.restricted]: {
            visibilityIcon: <PeopleAltIcon />,
            visibilityMessage: 'Only members and spaces added can open this link',
            visibilitySuccessMessage: `${target} is Restricted now`
        },
        [VisibiltyLevel.anyoneInOrg]: {
            visibilityIcon: <TeamIcon />,
            visibilityMessage: 'All members in this account can view',
            visibilitySuccessMessage: `${target} is shared with your organization now`
        },
        [VisibiltyLevel.anyone]: {
            visibilityIcon: <PublicIcon />,
            visibilityMessage: 'Anyone with the link can view',
            visibilitySuccessMessage: `${target} is Public now`
        }
    }

    return list[id as keyof typeof list]
}

type Props = {
    playbook: PlaybookType
    onChange?: () => void
    disabled?: boolean
    hideCopyLink?: boolean
    hideLinkTemplates?: boolean
}

const getActiveOption = (list: Array<OptionType>, isPublic?: boolean, isMainIncluded?: boolean) => {
    if (isPublic) return list.find(({ value }) => value === VisibiltyLevel.anyone)
    if (isMainIncluded) return list.find(({ value }) => value === VisibiltyLevel.anyoneInOrg)
    return list.find(({ value }) => value === VisibiltyLevel.restricted)
}

export const ShareLink = memo(
    ({
        playbook,
        onChange,
        disabled = false,
        hideCopyLink = false,
        hideLinkTemplates = false
    }: Props) => {
        const {
            isPlaylist,
            isPublic,
            id: playbookId,
            uploadedByUid,
            title,
            teams,
            isMainIncluded,
            orgName = 'Organization'
        } = playbook

        const classes = useStyles()

        const { uid, isPrivateAccount } = useAuth()

        const isOwner = uid === uploadedByUid

        const showConfirmation = useBoolean()
        const open = useBoolean()
        const startAt = useBoolean()

        const [startTime, setStartTime] = useState('00:00:00')
        const { currentTime } = useSelector(state => state.videoEditor)

        const { showSuccessNotification, showErrorNotification } = useNotification()
        const { copySharedLink } = useCopySharedLink()
        const { hasPrivacyControl } = useServiceUsage()

        const publicity = getPublicityList(orgName, isPrivateAccount)
        const [linkType, setLinkType] = useState(
            getActiveOption(publicity, isPublic, isMainIncluded) || publicity[0]
        )
        const target = isPlaylist ? 'Playlist' : 'Video'
        const { visibilityIcon, visibilityMessage } = getPublicityStaticData({
            id: linkType.value,
            target
        })

        const { disableExternalSharing } = useOrgFeatureFlags()

        const resetVisibilityOption = () =>
            setLinkType(getActiveOption(publicity, isPublic, isMainIncluded) || publicity[0])

        const { sharePlaybook, isSharing, ApprovalModal } = usePlaybookShare({
            onApprovalClose: resetVisibilityOption,
            onSuccess: (data, input) => {
                if (data?.code === 422) return

                showSuccessNotification(
                    getPublicityStaticData({ target, id: input.visibilityLevel })
                        .visibilitySuccessMessage
                )
                onChange?.()

                logVisibilityChanging({ playbook, visibilityLevel: input.visibilityLevel || '' })
                logToAnalytics('change_visibility', {
                    playbookType: playbook.mode,
                    isPublic: input.visibilityLevel === 'anyone',
                    visibilityLevel: input.visibilityLevel
                })
            },
            onFailure: (error: Error) => {
                resetVisibilityOption()
                console.error(error)
                showErrorNotification(
                    error?.message || 'Something went wrong. Please try again later'
                )
            }
        })

        const changePublicity = (visibilityLevel: ShareProps['visibilityLevel']) => {
            sharePlaybook({
                visibilityLevel,
                playbooksIds: [playbookId],
                shareMode: 'overwriteByOption'
            })
        }

        useEffect(() => {
            setStartTime(generateStartValue(currentTime))
        }, [currentTime])

        const isSharingEnabled = hasPrivacyControl || !isPublic

        return (
            <>
                <Box className={classes.shareLink} p={2}>
                    {isSharing && <LinearProgress className={classes.linearProgress} />}

                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                        style={{ opacity: isSharingEnabled ? 1 : 0.6 }}
                    >
                        <Tooltip
                            arrow
                            classes={{ popper: classes.popper }}
                            title={isSharingEnabled ? '' : 'Available on the Business Plan'}
                            placement="right"
                        >
                            <Box>
                                {!hideCopyLink && (
                                    <Typography>
                                        <b>Get shareable link</b>
                                    </Typography>
                                )}

                                <Box display="flex" alignItems="center">
                                    <Box mr={1} className={classes.roundIcon}>
                                        {visibilityIcon}
                                    </Box>
                                    <Box>
                                        <Select
                                            data-cy="visibility-select"
                                            value={linkType}
                                            onOpen={open.setTrue}
                                            onClose={open.setFalse}
                                            onChange={e => {
                                                const option = e.target.value as OptionType

                                                setLinkType(option)
                                                // If admin tries to change publicity from Public to Private and it was not published to any space / Main space -
                                                // then we need to show Confirmation dialog as after that Admin will not be able to open that playbook
                                                if (
                                                    !isOwner &&
                                                    !teams?.length &&
                                                    !isMainIncluded &&
                                                    option.value === VisibiltyLevel.restricted
                                                ) {
                                                    return showConfirmation.setTrue()
                                                }

                                                changePublicity(option.value)
                                            }}
                                            disabled={
                                                disabled ||
                                                isSharing ||
                                                disableExternalSharing ||
                                                !isSharingEnabled
                                            }
                                            className={classes.select}
                                            classes={{
                                                select: classes.selectedItem,
                                                icon: classes.icon
                                            }}
                                            MenuProps={{
                                                classes: {
                                                    paper: classes.selectRoot
                                                }
                                            }}
                                            renderValue={value => (value as OptionType).label}
                                        >
                                            {publicity.map(option => (
                                                <MenuItem
                                                    key={option.value}
                                                    // @ts-ignore value of type of OptionType
                                                    value={option}
                                                    data-cy={`${option.value}-item`}
                                                    divider={true}
                                                    disabled={
                                                        !hasPrivacyControl &&
                                                        option.value !== 'anyone'
                                                    }
                                                >
                                                    <SpacedGroup height={20}>
                                                        <Box width={20}>
                                                            {
                                                                getPublicityStaticData({
                                                                    id: option.value
                                                                }).visibilityIcon
                                                            }
                                                        </Box>

                                                        <Typography
                                                            variant="inherit"
                                                            style={{
                                                                fontSize: 14,
                                                                fontWeight: 500
                                                            }}
                                                        >
                                                            {option.label}
                                                        </Typography>
                                                    </SpacedGroup>
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        <Typography color="textSecondary" style={{ fontSize: 14 }}>
                                            {visibilityMessage}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Tooltip>

                        {!hideCopyLink && (
                            <ButtonGroup color="default">
                                <Button
                                    className={classes.copyLinkButton}
                                    startIcon={<LinkIcon />}
                                    onClick={() => {
                                        const timeParts = startTime
                                            .split(':')
                                            .map(part => Number(part.replaceAll('-', '')))
                                        const hoursToSeconds = timeParts[0] * 3600
                                        const minutesToSeconds = timeParts[1] * 60
                                        const seconds = timeParts[2]
                                        const mergedStartTime =
                                            hoursToSeconds + minutesToSeconds + seconds

                                        copySharedLink(
                                            playbook,
                                            true,
                                            {
                                                context: 'share-link'
                                            },
                                            startAt.isTrue ? `&t=${mergedStartTime}` : ''
                                        )
                                    }}
                                    data-cy="share-dialog-copy-link"
                                >
                                    <strong>Copy link</strong>
                                </Button>
                            </ButtonGroup>
                        )}
                    </Box>

                    {!hideLinkTemplates && (
                        <Box mt={2}>
                            <LinkTemplates playbook={playbook} />
                        </Box>
                    )}

                    {!hideCopyLink && (
                        <SpacedGroup mt={2}>
                            <label>
                                <SpacedGroup spacing={0}>
                                    <Checkbox
                                        size="small"
                                        color="primary"
                                        style={{ padding: '4px' }}
                                        value={startAt.isTrue}
                                        onChange={startAt.toggle}
                                    />
                                    Start at
                                </SpacedGroup>
                            </label>

                            <MaskedInput
                                mask="99:99:99"
                                maskPlaceholder="-"
                                placeholder="00:00:00"
                                disabled={startAt.isFalse}
                                value={startTime}
                                onChange={event => setStartTime(event.target.value)}
                            >
                                <TextField
                                    variant="outlined"
                                    style={{ width: '90px' }}
                                    size="small"
                                />
                            </MaskedInput>
                        </SpacedGroup>
                    )}
                </Box>

                {showConfirmation.isTrue && (
                    <ConfirmationDialog
                        isOpen
                        title="Make private"
                        onClose={() => {
                            showConfirmation.setFalse()
                            resetVisibilityOption()
                        }}
                        onConfirm={() => {
                            showConfirmation.setFalse()
                            changePublicity(linkType.value)
                        }}
                        text={
                            <>
                                Are you sure you want to make <b>{title} </b>
                                {target.toLowerCase()} private?
                            </>
                        }
                    />
                )}
                {ApprovalModal}
            </>
        )
    }
)
